import { useMemo, useEffect, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { TimeSignal } from 'graphql/types';
import { useFeatureFlag } from 'modules/common/hooks';

export const getDefaultValues = (
  notes: TimeSignal[]
): Record<string, boolean> => {
  const { days, notesKey } = notes.reduce(
    (acc, note) => {
      acc.notesKey[`${note.date}_${note.id}`] = false;
      acc.days.add(note.date);
      return acc;
    },
    {
      days: new Set<string>(),
      notesKey: {}
    } as { days: Set<string>; notesKey: Record<string, boolean> }
  );
  return {
    selectAll: false,
    ...Array.from(days).reduce((acc, day) => {
      acc[day] = false;
      return acc;
    }, {} as Record<string, boolean>),
    ...notesKey
  };
};

export const useNotesSelectionForm = (notes: TimeSignal[]): void => {
  const defaultValues = useMemo<Record<string, boolean>>(
    () => getDefaultValues(notes),
    [notes]
  );

  const { register, setValue, getValues } = useFormContext();

  useEffect(() => {
    Object.keys(defaultValues).forEach((key) => {
      register(key);
      const day = key.split('_', 2);
      const values = getValues();
      if (
        day.length === 2 &&
        typeof (values[key] ?? values[day[0]]) === 'boolean'
      ) {
        setValue(key, values[key]);
      } else {
        setValue(key, values[day[0]]);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);
};

export const useNotesSelectionReset = (): {
  unregisterKeys: (ids: string[]) => void;
  resetForm: () => void;
  clearSelectedNotes: () => void;
  clearForm: () => void;
} => {
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const {
    control,
    register,
    unregister,
    reset,
    setValue,
    getValues
  } = useFormContext();
  const values = useWatch({ control });

  const unregisterKeys = useCallback(
    async (ids: string[]) => {
      unregister(ids);
    },
    [unregister]
  );

  const clearForm = useCallback(() => {
    const currentValues = getValues();
    const ids = Object.keys(
      isTimeSignalFederatedSuggestionsEnabled ? currentValues : values
    );
    unregisterKeys(ids);
    reset({});
  }, [
    getValues,
    isTimeSignalFederatedSuggestionsEnabled,
    reset,
    unregisterKeys,
    values
  ]);
  const resetForm = useCallback(() => {
    const currentValues = getValues();
    const ids = Object.keys(
      isTimeSignalFederatedSuggestionsEnabled ? currentValues : values
    );
    reset({});
    ids.forEach((key) => register(key));
    ids.forEach((key) => setValue(key, false));
  }, [
    getValues,
    isTimeSignalFederatedSuggestionsEnabled,
    values,
    reset,
    register,
    setValue
  ]);

  const clearSelectedNotes = useCallback(() => {
    const currentValues = getValues();
    const ids = Object.keys(
      isTimeSignalFederatedSuggestionsEnabled ? currentValues : values
    );
    unregisterKeys(ids);
    resetForm();
  }, [
    getValues,
    isTimeSignalFederatedSuggestionsEnabled,
    resetForm,
    unregisterKeys,
    values
  ]);

  return {
    unregisterKeys: unregisterKeys,
    resetForm: resetForm,
    clearSelectedNotes,
    clearForm
  };
};
